import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './RegistrationForm.css';
function getOrCreateSessionToken() {
    if (typeof window === 'undefined') return null;
    let token = window.localStorage.getItem('study_session_token');
    if (!token) {
        // crypto.randomUUID is supported in modern browsers; if not, you could fallback
        token = crypto.randomUUID();
        window.localStorage.setItem('study_session_token', token);
    }
    return token;
}
function ConsentPage() {
    const navigate = useNavigate();
    const { search } = useLocation();
    const params = new URLSearchParams(search || '');
    const interfaceParam = params.get('interface') || params.get('mode') || 'write';
    const role = interfaceParam === 'annotate' ? 'evaluator' : 'creator';

    const [answers, setAnswers] = useState({
        readInfo: false,
        agree: '',      // 'yes' | 'no'
        signature: '',
    });

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [declined, setDeclined] = useState(false);

    const updateField = (field, value) =>
        setAnswers(prev => ({ ...prev, [field]: value }));

    const handleBackHome = () => navigate('/');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!answers.readInfo) {
            setError('Please confirm that you have read the information above.');
            return;
        }
        if (!answers.agree) {
            setError('Please indicate whether you agree to participate in this study.');
            return;
        }
        if (!answers.signature.trim()) {
            setError('Please type your name in the signature box.');
            return;
        }

        // If they explicitly do NOT consent, log (optional) and stop
        if (answers.agree === 'no') {
            setDeclined(true);
            try {
                await fetch('/api/consent', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        role,
                        consent_given: false,
                        signature: answers.signature.trim(),
                    }),
                });
            } catch (err) {
                console.error('Error logging non-consent:', err);
            }
            return;
        }

        setSubmitting(true);
        try {
            const pid = params.get('pid') || localStorage.getItem('pid') || '';
            const sessionToken = getOrCreateSessionToken();
            const res = await fetch('/api/consent', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    role,
                    consent_given: true,
                    signature: answers.signature.trim(),
                    session_token: sessionToken,
                    pid,
                }),
            });

            if (!res.ok) {
                throw new Error('Failed to submit consent.');
            }

            localStorage.setItem('consent_ok', '1');

            const sp = new URLSearchParams(search || '');
            sp.set('interface', interfaceParam);

            if (interfaceParam === 'write') {
                // Get pid/scenario from query first, fallback to localStorage
                const pid = sp.get('pid') || localStorage.getItem('pid');
                const scenario = sp.get('scenario') || localStorage.getItem('scenario') || 'human-only';

                sp.set('pid', pid);
                sp.set('scenario', scenario);
                sp.set('lock', '1');

                navigate({
                    pathname: '/write-poem',
                    search: `?${sp.toString()}`,
                });
            } else {
                const pid = sp.get('pid') || localStorage.getItem('pid');
                sp.set('pid', pid);

                navigate({
                    pathname: '/poems',
                    search: `?${sp.toString()}`,
                });
            }
        } catch (err) {
            console.error(err);
            setError(err.message || 'Server error while submitting consent.');
        } finally {
            setSubmitting(false);
        }
    };

    const CREATOR_PDF_URL =
        'https://drive.google.com/file/d/12zXEmJ8h3BYGXDk50J_jNf2cOZPoEU2y/preview';
    const EVALUATOR_PDF_URL =
        'https://drive.google.com/file/d/1J92qrbL2A03sT0YTA2UKYrS3xc7ylfAH/preview';

    const pdfUrl = role === 'creator' ? CREATOR_PDF_URL : EVALUATOR_PDF_URL;

    return (
        <div className="registration-page">
            <div className="registration-card">
                <button className="reg-back-link" onClick={handleBackHome}>
                    ← Back to Home
                </button>

                <div className="registration-header">
                    <div>
                        <h1 className="registration-title">
                            Letter of Information &amp; Consent – {role === 'creator' ? 'Creators' : 'Evaluators'}
                        </h1>
                        <p className="registration-subtitle">
                            Please read the information below carefully. You will then be asked to indicate
                            whether or not you consent to participate.
                        </p>
                    </div>
                    <div className="registration-badge">
                        {role === 'creator' ? 'Creator' : 'Evaluator'}
                    </div>
                </div>

                {declined && (
                    <div className="reg-error">
                        You have indicated that you do not consent to participate in this study.
                        You will not be able to continue. You may close this page at any time.
                    </div>
                )}

                {error && !declined && (
                    <div className="reg-error">
                        {error}
                    </div>
                )}

                {/* PDF viewer */}
                <div className="reg-field" style={{ marginTop: 10, marginBottom: 12 }}>
                    <iframe
                        title="Consent form"
                        src={pdfUrl}
                        width="100%"
                        height="600px"
                        style={{ border: '1px solid #e5e7eb', borderRadius: '12px' }}
                        allow="autoplay"
                    />
                </div>

                {!declined && (
                    <form className="registration-form" onSubmit={handleSubmit}>
                        {/* Single confirmation checkbox */}
                        <div className="reg-field">
                            <label className="reg-checkbox-option">
                                <input
                                    type="checkbox"
                                    checked={answers.readInfo}
                                    onChange={(e) => updateField('readInfo', e.target.checked)}
                                />
                                <span>
                I have read the information presented in the information letter about this study.
              </span>
                            </label>
                        </div>

                        {/* Consent decision */}
                        <div className="reg-field">
                            <div className="reg-label-row">
              <span className="reg-label">
                Do you consent to participate in this study?
              </span>
                                <span className="reg-required">*</span>
                            </div>
                            <div className="reg-radio-group">
                                <label className="reg-radio-option">
                                    <input
                                        type="radio"
                                        name="agree"
                                        value="yes"
                                        checked={answers.agree === 'yes'}
                                        onChange={() => updateField('agree', 'yes')}
                                    />
                                    <span>Yes, I consent to participate.</span>
                                </label>
                                <label className="reg-radio-option">
                                    <input
                                        type="radio"
                                        name="agree"
                                        value="no"
                                        checked={answers.agree === 'no'}
                                        onChange={() => updateField('agree', 'no')}
                                    />
                                    <span>No, I do not consent to participate.</span>
                                </label>
                            </div>
                        </div>

                        {/* Signature */}
                        <div className="reg-field">
                            <div className="reg-label-row">
                                <span className="reg-label">Signature (type your name)</span>
                                <span className="reg-required">*</span>
                            </div>
                            <input
                                type="text"
                                className="reg-input"
                                value={answers.signature}
                                onChange={(e) => updateField('signature', e.target.value)}
                                // placeholder="Type your full name"
                                placeholder="Type your AMT worker ID"

                            />
                        </div>

                        <div className="reg-footer">
            <span className="reg-status">
              {submitting ? 'Submitting…' : ''}
            </span>
                            <button
                                type="submit"
                                className="reg-button-primary"
                                disabled={submitting}
                            >
                                Submit Consent
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );

}



export default ConsentPage;